import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import PreviewRoundedIcon from '@mui/icons-material/PreviewRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AdminLayout, { AdminSection } from '../../components/AdminLayout';
import SchemaBodyFields from '../../components/SchemaBodyFields';
import axiosInstance from '../../utils/axiosInstance';
import { formatCapabilityLabel, uniqueSchemaKey } from '../../utils/capabilityContract';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'select', label: 'Single Selection' },
  { value: 'multiselect', label: 'Multiple Selection' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date and Time' },
  { value: 'file', label: 'File Upload' },
  { value: 'email', label: 'Email' },
  { value: 'url', label: 'URL' },
  { value: 'tags', label: 'Tags' },
  { value: 'instruction', label: 'Instruction' },
  { value: 'key_value_pairs', label: 'Key/Value Pairs' },
];

const MATCH_MODES = [
  { value: 'numeric_range', label: 'Numeric Range', types: ['number', 'range'] },
  { value: 'allowed_values', label: 'Supported Values', types: ['select', 'multiselect', 'tags'] },
  { value: 'boolean_support', label: 'Boolean Support', types: ['boolean'] },
];

const ROLE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'title', label: 'Request Title' },
  { value: 'need_by', label: 'Needed By Date' },
];

function listValues(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => (item && typeof item === 'object' ? item.value ?? item.key ?? item.label : item))
      .map((item) => String(item ?? '').trim())
      .filter(Boolean);
  }
  return String(value || '')
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function fieldTypeLabel(value) {
  return FIELD_TYPES.find((option) => option.value === value)?.label || formatCapabilityLabel(value);
}

function statusColor(status) {
  if (status === 'published' || status === 'active') {
    return 'success';
  }
  if (status === 'draft') {
    return 'warning';
  }
  return 'default';
}

function matchingForField(field) {
  const nested = field?.['x-amx']?.matching || {};
  const metadata = field?.metadata || {};
  const enabled = Boolean(
    nested.enabled ||
    metadata['x-amx-matchable'] ||
    metadata.x_amx_matchable ||
    field?.matchable,
  );
  const mode = nested.mode || metadata['x-amx-match-mode'] || metadata.x_amx_match_mode || field?.match_mode || '';
  return {
    enabled: Boolean(enabled && mode),
    mode,
  };
}

function matchingModesForType(type) {
  return MATCH_MODES.filter((mode) => mode.types.includes(type));
}

function blankFieldDraft() {
  return {
    label: '',
    name: '',
    description: '',
    type: 'text',
    required: false,
    options: '',
    unit: '',
    min: '',
    max: '',
    accept: '',
    multiple: false,
    role: '',
    matching_enabled: false,
    match_mode: '',
  };
}

function fieldDraftFromField(field) {
  const matching = matchingForField(field);
  return {
    ...blankFieldDraft(),
    ...field,
    label: field?.label || '',
    name: field?.name || '',
    description: field?.description || '',
    type: field?.type || 'text',
    required: Boolean(field?.required),
    options: Array.isArray(field?.options) ? listValues(field.options).join('\n') : '',
    unit: field?.unit || '',
    min: field?.min ?? field?.minimum ?? '',
    max: field?.max ?? field?.maximum ?? '',
    accept: Array.isArray(field?.accept) ? listValues(field.accept).join(', ') : field?.accept || '',
    multiple: Boolean(field?.multiple),
    role: field?.role || '',
    matching_enabled: matching.enabled,
    match_mode: matching.mode,
  };
}

function normalizeOptionalNumber(value, label) {
  if (value === '' || value === null || typeof value === 'undefined') {
    return undefined;
  }
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    throw new Error(`${label} must be a number.`);
  }
  return parsed;
}

function normalizeFieldDraft(draft, fields, editingIndex) {
  const label = String(draft.label || '').trim();
  if (!label) {
    throw new Error('Field Name is required.');
  }
  const type = draft.type || 'text';
  const existingKeys = fields
    .filter((_field, index) => index !== editingIndex)
    .map((field) => field.name)
    .filter(Boolean);
  const existingField = editingIndex >= 0 ? fields[editingIndex] : null;
  const name = existingField?.name || draft.name || uniqueSchemaKey(label, existingKeys, 'field');
  if (!/^[A-Za-z0-9_][A-Za-z0-9_-]*$/.test(name)) {
    throw new Error('Field keys can only use letters, numbers, underscores, and hyphens.');
  }
  if (existingKeys.includes(name)) {
    throw new Error('Field keys must be unique.');
  }
  const normalized = {
    label,
    name,
    required: Boolean(draft.required),
    type,
    display_order: editingIndex >= 0 ? editingIndex : fields.length,
  };
  const description = String(draft.description || '').trim();
  if (description) {
    normalized.description = description;
  }
  if (['select', 'multiselect', 'tags'].includes(type)) {
    const options = listValues(draft.options);
    if (!options.length) {
      throw new Error('Selection fields need at least one option.');
    }
    normalized.options = options;
  }
  if (type === 'number' || type === 'range') {
    const min = normalizeOptionalNumber(draft.min, `${label} minimum`);
    const max = normalizeOptionalNumber(draft.max, `${label} maximum`);
    if (typeof min !== 'undefined' && typeof max !== 'undefined' && min > max) {
      throw new Error(`${label} minimum cannot exceed maximum.`);
    }
    if (typeof min !== 'undefined') {
      normalized.min = min;
    }
    if (typeof max !== 'undefined') {
      normalized.max = max;
    }
    if (String(draft.unit || '').trim()) {
      normalized.unit = String(draft.unit || '').trim();
    }
  }
  if (type === 'file') {
    const accept = listValues(draft.accept).join(',');
    if (accept) {
      normalized.accept = accept;
    }
    normalized.multiple = Boolean(draft.multiple);
  }
  if (draft.role) {
    normalized.role = draft.role;
  }
  if (draft.matching_enabled) {
    const allowedModes = matchingModesForType(type);
    const matchMode = draft.match_mode || allowedModes[0]?.value || '';
    if (!allowedModes.some((mode) => mode.value === matchMode)) {
      throw new Error('Matching behavior is not compatible with this field type.');
    }
    normalized.metadata = {
      ...(existingField?.metadata && typeof existingField.metadata === 'object' ? existingField.metadata : {}),
      'x-amx-matchable': true,
      'x-amx-match-mode': matchMode,
    };
    normalized['x-amx'] = {
      ...(existingField?.['x-amx'] && typeof existingField['x-amx'] === 'object' ? existingField['x-amx'] : {}),
      matching: {
        enabled: true,
        mode: matchMode,
      },
    };
  }
  return normalized;
}

function withDisplayOrder(fields) {
  return fields.map((field, index) => ({ ...field, display_order: index }));
}

function moveField(fields, index, direction) {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= fields.length) {
    return fields;
  }
  const nextFields = [...fields];
  const [field] = nextFields.splice(index, 1);
  nextFields.splice(nextIndex, 0, field);
  return withDisplayOrder(nextFields);
}

function FieldDialog({ open, draft, isEditing, error, onChange, onClose, onSave }) {
  if (!draft) {
    return null;
  }
  const availableModes = matchingModesForType(draft.type);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEditing ? 'Edit Request Field' : 'Add Request Field'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label="Field Name"
            value={draft.label}
            onChange={(event) => onChange({ label: event.target.value })}
            required
            fullWidth
          />
          <TextField
            label="Description"
            value={draft.description}
            onChange={(event) => onChange({ description: event.target.value })}
            fullWidth
            multiline
            minRows={3}
          />
          <TextField
            select
            label="Type"
            value={draft.type}
            onChange={(event) => {
              const nextType = event.target.value;
              const nextModes = matchingModesForType(nextType);
              onChange({
                type: nextType,
                matching_enabled: draft.matching_enabled && nextModes.length > 0,
                match_mode: nextModes.some((mode) => mode.value === draft.match_mode)
                  ? draft.match_mode
                  : nextModes[0]?.value || '',
              });
            }}
            fullWidth
          >
            {FIELD_TYPES.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={<Checkbox checked={Boolean(draft.required)} onChange={(event) => onChange({ required: event.target.checked })} />}
            label="Required"
          />
          {['select', 'multiselect', 'tags'].includes(draft.type) ? (
            <TextField
              label="Options"
              value={draft.options}
              onChange={(event) => onChange({ options: event.target.value })}
              fullWidth
              multiline
              minRows={3}
            />
          ) : null}
          {draft.type === 'number' || draft.type === 'range' ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField label="Unit" value={draft.unit} onChange={(event) => onChange({ unit: event.target.value })} fullWidth />
              <TextField label="Minimum" type="number" value={draft.min} onChange={(event) => onChange({ min: event.target.value })} fullWidth />
              <TextField label="Maximum" type="number" value={draft.max} onChange={(event) => onChange({ max: event.target.value })} fullWidth />
            </Stack>
          ) : null}
          {draft.type === 'file' ? (
            <Stack spacing={1}>
              <TextField
                label="Accepted File Types"
                value={draft.accept}
                onChange={(event) => onChange({ accept: event.target.value })}
                fullWidth
              />
              <FormControlLabel
                control={<Checkbox checked={Boolean(draft.multiple)} onChange={(event) => onChange({ multiple: event.target.checked })} />}
                label="Allow multiple files"
              />
            </Stack>
          ) : null}
          <Divider />
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Capability Matching
            </Typography>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={Boolean(draft.matching_enabled)}
                  disabled={!availableModes.length}
                  onChange={(event) => onChange({
                    matching_enabled: event.target.checked,
                    match_mode: draft.match_mode || availableModes[0]?.value || '',
                  })}
                />
              )}
              label="Use for Capability Matching"
            />
            {draft.matching_enabled && availableModes.length ? (
              <TextField
                select
                label="Matching Behavior"
                value={draft.match_mode || availableModes[0]?.value || ''}
                onChange={(event) => onChange({ match_mode: event.target.value })}
                fullWidth
              >
                {availableModes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            ) : null}
          </Stack>
          <Divider />
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Advanced
            </Typography>
            <TextField
              label="Stable Key"
              value={draft.name}
              onChange={(event) => onChange({ name: event.target.value })}
              fullWidth
              disabled={isEditing}
            />
            <TextField
              select
              label="AMX Field Role"
              value={draft.role}
              onChange={(event) => onChange({ role: event.target.value })}
              fullWidth
            >
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onSave}>{isEditing ? 'Save' : 'Add Field'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function PreviewDialog({ open, onClose, fields }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Preview Request Form</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">This is what a requester will see.</Alert>
          <SchemaBodyFields fields={fields} disabled />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdminNeedCategoryDetailPage(props) {
  const { categoryId } = useParams();
  const [tab, setTab] = useState('overview');
  const [detail, setDetail] = useState(null);
  const [fields, setFields] = useState([]);
  const [overview, setOverview] = useState({ name: '', code: '', description: '', status: 'draft' });
  const [fieldDialog, setFieldDialog] = useState(null);
  const [fieldError, setFieldError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');

  const category = detail?.need_category || null;
  const versions = detail?.schema_versions || [];
  const editableSchema = category?.editable_schema || null;
  const currentSchema = category?.current_schema || null;
  const draftVersion = editableSchema?.version || '';
  const isDraftEditable = editableSchema?.status === 'draft';
  const matchableFields = useMemo(() => fields.filter((field) => matchingForField(field).enabled), [fields]);

  function applyDetail(nextDetail) {
    const nextCategory = nextDetail?.need_category || null;
    setDetail(nextDetail);
    setFields(withDisplayOrder(Array.isArray(nextDetail?.schema?.body) ? nextDetail.schema.body : []));
    setOverview({
      name: nextCategory?.category_display_name || '',
      code: nextCategory?.category_name || '',
      description: nextCategory?.description || '',
      status: nextCategory?.status || 'draft',
    });
    setDirty(false);
  }

  async function loadDetail() {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get(`/admin/need-categories/${encodeURIComponent(categoryId)}`);
      applyDetail(response.data);
    } catch (err) {
      setDetail(null);
      setError(err?.response?.data?.message || err?.message || 'Unable to load need category.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDetail();
  }, [categoryId]);

  function updateFields(nextFields) {
    setFields(withDisplayOrder(nextFields));
    setDirty(true);
  }

  function openNewField() {
    setFieldError('');
    setFieldDialog({ index: -1, draft: blankFieldDraft() });
  }

  function openEditField(field, index) {
    setFieldError('');
    setFieldDialog({ index, draft: fieldDraftFromField(field) });
  }

  function closeFieldDialog() {
    setFieldDialog(null);
    setFieldError('');
  }

  function updateFieldDraft(patch) {
    setFieldDialog((current) => current ? ({ ...current, draft: { ...current.draft, ...patch } }) : current);
  }

  function saveFieldDraft() {
    try {
      const normalized = normalizeFieldDraft(fieldDialog.draft, fields, fieldDialog.index);
      const nextFields = fieldDialog.index >= 0
        ? fields.map((field, index) => (index === fieldDialog.index ? normalized : field))
        : [...fields, normalized];
      updateFields(nextFields);
      closeFieldDialog();
    } catch (err) {
      setFieldError(err?.message || 'Unable to save request field.');
    }
  }

  async function saveDraft(nextFields = fields) {
    if (!draftVersion) {
      throw new Error('No draft schema version is available.');
    }
    setSaving(true);
    setError('');
    try {
      const response = await axiosInstance.put(
        `/admin/need-categories/${encodeURIComponent(categoryId)}/schema-versions/${encodeURIComponent(draftVersion)}/draft`,
        { body: withDisplayOrder(nextFields) },
      );
      applyDetail(response.data);
      setFeedback('Draft saved.');
      return response.data;
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to save draft.');
      throw err;
    } finally {
      setSaving(false);
    }
  }

  async function saveOverview() {
    setSaving(true);
    setError('');
    try {
      const response = await axiosInstance.patch(`/admin/need-categories/${encodeURIComponent(categoryId)}`, overview);
      applyDetail(response.data);
      setFeedback('Need category updated.');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to update need category.');
    } finally {
      setSaving(false);
    }
  }

  async function publishDraft() {
    if (!draftVersion) {
      return;
    }
    const versionToPublish = draftVersion;
    setSaving(true);
    setError('');
    try {
      if (dirty) {
        await saveDraft(fields);
      }
      const response = await axiosInstance.post(
        `/admin/need-categories/${encodeURIComponent(categoryId)}/schema-versions/${encodeURIComponent(versionToPublish)}/publish`,
      );
      applyDetail(response.data);
      setFeedback(`Published version ${versionToPublish}.`);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to publish schema version.');
    } finally {
      setSaving(false);
    }
  }

  async function createDraftFromCurrent() {
    setSaving(true);
    setError('');
    try {
      const response = await axiosInstance.post(`/admin/need-categories/${encodeURIComponent(categoryId)}/schema-versions/draft`);
      applyDetail(response.data);
      setFeedback('Draft version created.');
      setTab('request-form');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to create draft version.');
    } finally {
      setSaving(false);
    }
  }

  async function retireVersion(version) {
    if (!window.confirm(`Retire schema version ${version}?`)) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      const response = await axiosInstance.post(
        `/admin/need-categories/${encodeURIComponent(categoryId)}/schema-versions/${encodeURIComponent(version)}/retire`,
      );
      applyDetail(response.data);
      setFeedback(`Retired version ${version}.`);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to retire schema version.');
    } finally {
      setSaving(false);
    }
  }

  const breadcrumbItems = [
    { label: 'AMX xConsole', href: '/' },
    { label: 'Admin Dashboard', href: '/admin' },
    { label: 'Need Categories', href: '/admin/need-categories' },
    { label: category?.category_display_name || 'Need Category' },
  ];

  return (
    <AdminLayout
      props={props}
      breadcrumbItems={breadcrumbItems}
      title={category?.category_display_name || 'Need Category'}
      subtitle={category?.description || 'Manage this federation request form.'}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
        <Button component={RouterLink} to="/admin/need-categories" startIcon={<ArrowBackRoundedIcon />}>
          Back
        </Button>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {draftVersion ? (
            <Button variant="contained" startIcon={<CheckCircleRoundedIcon />} onClick={publishDraft} disabled={saving || !fields.length}>
              Publish v{draftVersion}
            </Button>
          ) : currentSchema ? (
            <Button variant="outlined" startIcon={<EditRoundedIcon />} onClick={createDraftFromCurrent} disabled={saving}>
              Edit Request Form
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {feedback ? <Alert severity="success" onClose={() => setFeedback('')}>{feedback}</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      <AdminSection
        title="Need Category Contract"
        subtitle={currentSchema?.version ? `Current published version ${currentSchema.version}` : 'No published version yet.'}
      >
        {loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <span>Loading need category...</span>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Tabs value={tab} onChange={(_event, nextTab) => setTab(nextTab)}>
              <Tab value="overview" label="Overview" />
              <Tab value="request-form" label="Request Form" />
              <Tab value="matching" label="Matching" />
              <Tab value="versions" label="Versions" />
            </Tabs>

            {tab === 'overview' ? (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField label="Name" value={overview.name} onChange={(event) => setOverview({ ...overview, name: event.target.value })} fullWidth required />
                  <TextField label="Code" value={overview.code} onChange={(event) => setOverview({ ...overview, code: event.target.value })} fullWidth />
                  <TextField select label="Status" value={overview.status} onChange={(event) => setOverview({ ...overview, status: event.target.value })} fullWidth>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="deprecated">Deprecated</MenuItem>
                    <MenuItem value="retired">Retired</MenuItem>
                  </TextField>
                </Stack>
                <TextField label="Description" value={overview.description} onChange={(event) => setOverview({ ...overview, description: event.target.value })} fullWidth multiline minRows={3} required />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip color={statusColor(category?.status)} label={category?.status || 'draft'} />
                  <Chip label={`Current Version: ${currentSchema?.version || '-'}`} />
                  <Chip label={`Fields: ${fields.length}`} />
                  <Chip label={`Matching Fields: ${matchableFields.length}`} />
                </Stack>
                <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={saveOverview} disabled={saving || !overview.name.trim() || !overview.description.trim()} sx={{ alignSelf: 'flex-start' }}>
                  Save Overview
                </Button>
              </Stack>
            ) : null}

            {tab === 'request-form' ? (
              <Stack spacing={2}>
                {!isDraftEditable ? (
                  <Alert severity="info">
                    Published schema versions are immutable. Create a draft to edit this request form.
                  </Alert>
                ) : dirty ? (
                  <Alert severity="warning">Draft has unsaved changes.</Alert>
                ) : null}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button variant="outlined" startIcon={<PreviewRoundedIcon />} onClick={() => setPreviewOpen(true)}>
                      Preview Request Form
                    </Button>
                    {isDraftEditable ? (
                      <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={() => saveDraft()} disabled={saving || !dirty}>
                        Save Draft
                      </Button>
                    ) : null}
                  </Stack>
                  {isDraftEditable ? (
                    <Button variant="contained" startIcon={<AddCircleRoundedIcon />} onClick={openNewField}>
                      Add Field
                    </Button>
                  ) : null}
                </Stack>
                <Stack divider={<Divider />}>
                  {fields.length ? fields.map((field, index) => {
                    const matching = matchingForField(field);
                    return (
                      <Stack
                        key={field.name || index}
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.25}
                        justifyContent="space-between"
                        sx={{ py: 1.5 }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
                            {field.label || field.name}
                          </Typography>
                          {field.description ? (
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                              {field.description}
                            </Typography>
                          ) : null}
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                            <Chip size="small" label={fieldTypeLabel(field.type)} variant="outlined" />
                            <Chip size="small" color={field.required ? 'primary' : 'default'} variant={field.required ? 'filled' : 'outlined'} label={field.required ? 'Required' : 'Optional'} />
                            {matching.enabled ? <Chip size="small" color="success" label={MATCH_MODES.find((mode) => mode.value === matching.mode)?.label || matching.mode} /> : null}
                            {field.role ? <Chip size="small" label={ROLE_OPTIONS.find((role) => role.value === field.role)?.label || field.role} variant="outlined" /> : null}
                          </Stack>
                        </Box>
                        {isDraftEditable ? (
                          <Stack direction="row" spacing={0.5} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                            <Tooltip title="Move up">
                              <span>
                                <IconButton size="small" disabled={index === 0} onClick={() => updateFields(moveField(fields, index, -1))}>
                                  <ArrowUpwardRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Move down">
                              <span>
                                <IconButton size="small" disabled={index === fields.length - 1} onClick={() => updateFields(moveField(fields, index, 1))}>
                                  <ArrowDownwardRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Edit field">
                              <IconButton size="small" onClick={() => openEditField(field, index)}>
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove field">
                              <IconButton size="small" color="error" onClick={() => updateFields(fields.filter((_item, itemIndex) => itemIndex !== index))}>
                                <DeleteRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : null}
                      </Stack>
                    );
                  }) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No request fields are defined yet.
                    </Typography>
                  )}
                </Stack>
              </Stack>
            ) : null}

            {tab === 'matching' ? (
              <Stack spacing={2}>
                <Alert severity={matchableFields.length ? 'success' : 'info'}>
                  {matchableFields.length} fields participate in deterministic matching.
                </Alert>
                <Stack divider={<Divider />}>
                  {matchableFields.length ? matchableFields.map((field) => {
                    const matching = matchingForField(field);
                    return (
                      <Stack key={field.name} direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" sx={{ py: 1.25 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {field.label || field.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {fieldTypeLabel(field.type)}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip color="success" label={MATCH_MODES.find((mode) => mode.value === matching.mode)?.label || matching.mode} />
                          {isDraftEditable ? (
                            <Button size="small" variant="outlined" onClick={() => openEditField(field, fields.findIndex((item) => item.name === field.name))}>
                              Edit
                            </Button>
                          ) : null}
                        </Stack>
                      </Stack>
                    );
                  }) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No fields are enabled for deterministic matching.
                    </Typography>
                  )}
                </Stack>
              </Stack>
            ) : null}

            {tab === 'versions' ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Version</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Fields</TableCell>
                    <TableCell>Matching Fields</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Published</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {versions.map((version) => (
                    <TableRow key={`${version.schema_id}-${version.version}`}>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {version.version}
                          </Typography>
                          {version.is_current_published ? <Chip size="small" color="success" label="Current" /> : null}
                        </Stack>
                      </TableCell>
                      <TableCell><Chip size="small" color={statusColor(version.status)} label={version.status} /></TableCell>
                      <TableCell>{version.field_count ?? 0}</TableCell>
                      <TableCell>{version.matchable_field_count ?? 0}</TableCell>
                      <TableCell>{version.source || '-'}</TableCell>
                      <TableCell>{version.published_at || '-'}</TableCell>
                      <TableCell align="right">
                        {version.status === 'published' && !version.is_current_published ? (
                          <Button size="small" variant="outlined" startIcon={<HistoryRoundedIcon />} onClick={() => retireVersion(version.version)} disabled={saving}>
                            Retire
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : null}
          </Stack>
        )}
      </AdminSection>

      <FieldDialog
        open={Boolean(fieldDialog)}
        draft={fieldDialog?.draft}
        isEditing={fieldDialog?.index >= 0}
        error={fieldError}
        onChange={updateFieldDraft}
        onClose={closeFieldDialog}
        onSave={saveFieldDraft}
      />
      <PreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} fields={fields} />
    </AdminLayout>
  );
}
